import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import '../css/Payouts.css';
import '../css/Shimmer.css';
import { payoutsAPI } from '../services/api';
import { ShimmerStatCards, ShimmerTable } from '../components/Shimmer';
import CustomAlert from '../components/CustomAlert';

// Import icons
import totalPaidIcon from '../assets/images/Driverimages/total-driver.svg';
import scheduledIcon from '../assets/images/Driverimages/active-driver.svg';
import completedJobsIcon from '../assets/images/Driverimages/completed-jobs.svg';
import commissionIcon from '../assets/images/Driverimages/av-driver-rating.svg';
import scheduleIcon from '../assets/images/Formicons/shedule.svg';
import payoutsEmptyIcon from '../assets/images/EmptyIcons/Payouts-empty.svg';

const Payouts = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromDateISO, setFromDateISO] = useState('');
  const [toDateISO, setToDateISO] = useState('');
  const [status, setStatus] = useState('');
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);

  // Loading states
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [payoutsLoading, setPayoutsLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // API data states
  const [summary, setSummary] = useState(null);
  const [payoutsData, setPayoutsData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Date picker states
  const [showFromDatePicker, setShowFromDatePicker] = useState(false);
  const [showToDatePicker, setShowToDatePicker] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Refs for click outside
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  // CustomAlert state
  const [customAlert, setCustomAlert] = useState({
    show: false,
    type: 'success',
    title: '',
    message: ''
  });

  const showCustomAlert = (type, title, message) => {
    setCustomAlert({ show: true, type, title, message });
  };

  const closeCustomAlert = () => {
    setCustomAlert(prev => ({ ...prev, show: false }));
  };

  // Fetch data on mount
  useEffect(() => {
    fetchSummary();
    fetchPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch payouts when page changes
  useEffect(() => {
    if (currentPage > 1) {
      fetchPayouts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Fetch summary data
  const fetchSummary = async () => {
    try {
      setSummaryLoading(true);
      const response = await payoutsAPI.getSummary();
      if (response.data.Status === 'Success') {
        setSummary(response.data.Summary);
      }
    } catch (error) {
      // Error fetching summary
    } finally {
      setSummaryLoading(false);
    }
  };

  // Fetch payouts list
  const fetchPayouts = async (filters = {}) => {
    try {
      setPayoutsLoading(true);
      const params = {
        page: currentPage,
        ...filters
      };
      const response = await payoutsAPI.getPayouts(params);
      if (response.data.Status === 'Success') {
        setPayoutsData(response.data.Payouts || []);
        setPagination(response.data.Pagination);
      }
    } catch (error) {
      // Error fetching payouts
    } finally {
      setPayoutsLoading(false);
    }
  };

  // Fetch payout details
  const fetchPayoutDetails = async (payoutId) => {
    try {
      setDetailsLoading(true);
      const response = await payoutsAPI.getPayoutDetails(payoutId);
      if (response.data.Status === 'Success') {
        return response.data.Payout;
      }
      return null;
    } catch (error) {
      return null;
    } finally {
      setDetailsLoading(false);
    }
  };

  // Click outside to close date pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromDateRef.current && !fromDateRef.current.contains(event.target)) {
        setShowFromDatePicker(false);
      }
      if (toDateRef.current && !toDateRef.current.contains(event.target)) {
        setShowToDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle View button click
  const handleViewPayout = async (payout) => {
    // Fetch detailed payout info from API
    const details = await fetchPayoutDetails(payout.PayoutId);

    if (details) {
      setSelectedPayout({
        id: payout.PayoutId,
        period: payout.Period,
        transactionId: details.TransactionId || payout.TransactionId || '-',
        remittanceDate: details.RemittanceDate || payout.RemittanceDate || '-',
        totalRides: details.TotalRides || payout.TotalRides || 0,
        totalEarnings: details.TotalEarnings || payout.Earnings || 0,
        deduction: details.Deduction || payout.Deduction || 0,
        totalPayout: details.TotalPayout || payout.Payout || 0,
        status: details.Status || payout.Status,
        releasedAt: details.ReleasedAt || payout.ReleasedAt,
        items: details.Items || [],
        itemsCount: details.ItemsCount || 0
      });
    } else {
      // Fallback to list data if details API fails
      setSelectedPayout({
        id: payout.PayoutId,
        period: payout.Period,
        transactionId: payout.TransactionId || '-',
        remittanceDate: payout.RemittanceDate || '-',
        totalRides: payout.TotalRides || 0,
        totalEarnings: payout.Earnings || 0,
        deduction: payout.Deduction || 0,
        totalPayout: payout.Payout || 0,
        status: payout.Status,
        releasedAt: payout.ReleasedAt,
        items: [],
        itemsCount: 0
      });
    }

    setShowPayoutModal(true);
    document.body.style.overflow = 'hidden';
  };

  // Close modal
  const handleCloseModal = () => {
    setShowPayoutModal(false);
    setSelectedPayout(null);
    document.body.style.overflow = 'unset';
  };

  // Handle Invoice download
  const handleDownloadInvoice = async () => {
    if (!selectedPayout?.id) return;

    try {
      setInvoiceLoading(true);
      const response = await payoutsAPI.downloadInvoice(selectedPayout.id);

      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Payout_${selectedPayout.id}_Invoice.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showCustomAlert('error', 'Download Failed', 'Failed to download invoice. Please try again.');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Date picker helper functions
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDate = (date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateISO = (date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const previousMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const nextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const generateCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const handleFromDateSelect = (day) => {
    const selected = new Date(selectedYear, selectedMonth, day);
    setFromDate(formatDate(selected));
    setFromDateISO(formatDateISO(selected));
    setShowFromDatePicker(false);
  };

  const handleToDateSelect = (day) => {
    const selected = new Date(selectedYear, selectedMonth, day);
    setToDate(formatDate(selected));
    setToDateISO(formatDateISO(selected));
    setShowToDatePicker(false);
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Format currency
  const formatCurrency = (value) => {
    return `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Generate stats cards from API summary
  const getStatsCards = () => {
    return [
      {
        id: 1,
        title: 'Total Paid',
        value: formatCurrency(summary?.TotalPaid),
        borderColor: '#6366F1',
        icon: totalPaidIcon
      },
      {
        id: 2,
        title: 'Pending Payout',
        value: formatCurrency(summary?.TotalPending),
        borderColor: '#EC4899',
        icon: scheduledIcon
      },
      {
        id: 3,
        title: 'Completed Rides',
        value: summary?.TotalRides || 0,
        borderColor: '#F59E0B',
        icon: completedJobsIcon
      },
      {
        id: 4,
        title: 'Total Deduction',
        value: formatCurrency(summary?.TotalDeduction),
        borderColor: '#10B981',
        icon: commissionIcon
      }
    ];
  };

  const statsCards = getStatsCards();

  // Filter function
  const handleGetDetails = () => {
    const filters = {};

    if (fromDateISO) {
      filters.from_date = fromDateISO;
    }
    if (toDateISO) {
      filters.to_date = toDateISO;
    }
    if (status) {
      filters.status = status;
    }

    setCurrentPage(1);
    setFiltersApplied(true);
    fetchPayouts(filters);
  };

  // Clear filters
  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
    setFromDateISO('');
    setToDateISO('');
    setStatus('');
    setCurrentPage(1);
    setFiltersApplied(false);
    fetchPayouts();
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'released' || statusLower === 'paid') return 'paid';
    if (statusLower === 'pending') return 'on-hold';
    if (statusLower === 'failed') return 'failed';
    return statusLower?.replace(' ', '-');
  };

  // Get status display text
  const getStatusDisplay = (status, statusDisplay) => {
    if (statusDisplay) return statusDisplay;
    if (status === 'RELEASED') return 'Paid';
    if (status === 'PENDING') return 'Pending';
    return status;
  };

  return (
    <div className="payouts-page">
      <Container fluid className="px-4 py-4">
        {/* Stats Cards Row */}
        {summaryLoading ? (
          <ShimmerStatCards count={4} />
        ) : (
          <Row className="mb-4">
            {statsCards.map((card) => (
              <Col lg={3} md={6} sm={6} xs={12} key={card.id} className="mb-3">
                <div
                  className="payouts-stat-card"
                  style={{ borderLeft: `4px solid ${card.borderColor}` }}
                >
                  <div className="payouts-stat-content">
                    <p className="payouts-stat-title">{card.title}</p>
                    <h2 className="payouts-stat-value">{card.value}</h2>
                  </div>
                  <div className="payouts-stat-icon">
                    <img src={card.icon} alt={card.title} />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}

        {/* Filter Section */}
        <div className="payouts-filter-card mb-4">
          <div className="payouts-filter-content">
            <h3 className="payouts-filter-title">All Transaction Statement</h3>

            <div className="payouts-filter-controls">
              {/* From Date - Custom Picker */}
              <div className="payouts-date-picker" ref={fromDateRef}>
                <div
                  className="payouts-date-field"
                  onClick={() => {
                    setShowFromDatePicker(!showFromDatePicker);
                    setShowToDatePicker(false);
                  }}
                >
                  <img src={scheduleIcon} alt="" className="date-field-icon" />
                  <span className={fromDate ? 'date-value' : 'date-placeholder'}>
                    {fromDate || 'From'}
                  </span>
                </div>

                {showFromDatePicker && (
                  <div className="payouts-calendar-dropdown">
                    <div className="calendar-header">
                      <button className="calendar-nav-btn" onClick={previousMonth}>‹</button>
                      <span className="calendar-month-year">
                        {monthNames[selectedMonth]} {selectedYear}
                      </span>
                      <button className="calendar-nav-btn" onClick={nextMonth}>›</button>
                    </div>

                    <div className="calendar-weekdays">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                        <div key={index} className="calendar-weekday">{day}</div>
                      ))}
                    </div>

                    <div className="calendar-days">
                      {generateCalendar().map((day, index) => (
                        <div
                          key={index}
                          className={`calendar-day ${day ? 'active' : 'empty'} ${
                            fromDate === formatDate(new Date(selectedYear, selectedMonth, day)) ? 'selected' : ''
                          }`}
                          onClick={() => day && handleFromDateSelect(day)}
                        >
                          {day || ''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* To Date - Custom Picker */}
              <div className="payouts-date-picker" ref={toDateRef}>
                <div
                  className="payouts-date-field"
                  onClick={() => {
                    setShowToDatePicker(!showToDatePicker);
                    setShowFromDatePicker(false);
                  }}
                >
                  <img src={scheduleIcon} alt="" className="date-field-icon" />
                  <span className={toDate ? 'date-value' : 'date-placeholder'}>
                    {toDate || 'To'}
                  </span>
                </div>

                {showToDatePicker && (
                  <div className="payouts-calendar-dropdown">
                    <div className="calendar-header">
                      <button className="calendar-nav-btn" onClick={previousMonth}>‹</button>
                      <span className="calendar-month-year">
                        {monthNames[selectedMonth]} {selectedYear}
                      </span>
                      <button className="calendar-nav-btn" onClick={nextMonth}>›</button>
                    </div>

                    <div className="calendar-weekdays">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                        <div key={index} className="calendar-weekday">{day}</div>
                      ))}
                    </div>

                    <div className="calendar-days">
                      {generateCalendar().map((day, index) => (
                        <div
                          key={index}
                          className={`calendar-day ${day ? 'active' : 'empty'} ${
                            toDate === formatDate(new Date(selectedYear, selectedMonth, day)) ? 'selected' : ''
                          }`}
                          onClick={() => day && handleToDateSelect(day)}
                        >
                          {day || ''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Status Dropdown */}
              <div className="payouts-status-dropdown">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Status</option>
                  <option value="RELEASED">Paid</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>

              {/* Get Details Button */}
              <button
                className={`payouts-get-details-btn ${!fromDate && !toDate && !status ? 'disabled' : ''}`}
                onClick={handleGetDetails}
                disabled={!fromDate && !toDate && !status}
              >
                GET DETAILS
              </button>

              {/* Clear Filter Button - show only when filters applied */}
              {filtersApplied && (
                <button className="payouts-clear-btn" onClick={handleClearFilters}>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Payouts Table */}
        <div className="payouts-table-card">
          {payoutsLoading ? (
            <ShimmerTable rows={5} columns={5} />
          ) : (
            <>
              <table className="payouts-table">
                <thead>
                  <tr>
                    <th>Payout ID</th>
                    <th>Period</th>
                    <th>Earnings</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutsData.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty-table-cell">
                        <div className="payouts-empty-state">
                          <img src={payoutsEmptyIcon} alt="No Payouts" className="empty-state-icon" />
                          <h3 className="empty-state-title">No Payouts Found</h3>
                          <p className="empty-state-text">
                            {filtersApplied
                              ? 'No payouts found for selected filters. Try adjusting your filters or check back later.'
                              : 'You don\'t have any payouts yet. Payouts will appear here once payments are processed.'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : payoutsData.map((row, index) => (
                    <tr key={row.PayoutId || index}>
                      <td>
                        <div className="payout-id-cell">
                          <span className="payout-id">#{row.PayoutId}</span>
                          {row.TransactionId && (
                            <span className="payout-utr">UTR: {row.TransactionId}</span>
                          )}
                        </div>
                      </td>
                      <td>{row.Period}</td>
                      <td>{formatCurrency(row.Payout)}</td>
                      <td>
                        <span className={`payouts-status-badge ${getStatusBadgeClass(row.Status)}`}>
                          {getStatusDisplay(row.Status, row.StatusDisplay)}
                        </span>
                      </td>
                      <td>
                        <button
                          className="payouts-view-btn"
                          onClick={() => handleViewPayout(row)}
                          disabled={detailsLoading}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {pagination && pagination.TotalPages > 1 && (
                <div className="payouts-pagination">
                  <button
                    className="pagination-btn"
                    disabled={!pagination.HasPrevious}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination.CurrentPage} of {pagination.TotalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={!pagination.HasNext}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Results count when filtered */}
        {filtersApplied && pagination && (
          <p className="payouts-results-count">
            Showing {payoutsData.length} of {pagination.TotalCount} payouts
          </p>
        )}
      </Container>

      {/* Payout Details Modal */}
      {showPayoutModal && selectedPayout && (
        <div className="payout-modal-overlay" onClick={handleCloseModal}>
          <div className="payout-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="payout-modal-header">
              <h3 className="payout-modal-title">Payout ID: #{selectedPayout.id}</h3>
              <div className="payout-modal-actions">
                <button
                  className="payout-invoice-btn"
                  onClick={handleDownloadInvoice}
                  disabled={invoiceLoading}
                >
                  {invoiceLoading ? 'Downloading...' : 'Invoice'}
                  {!invoiceLoading && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 12L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 8L8 12L4 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 14H13" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  )}
                </button>
                <button className="payout-close-btn" onClick={handleCloseModal}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L13 13M1 13L13 1" stroke="#011A42" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="payout-modal-body">
              <div className="payout-detail-row">
                <span className="payout-detail-label">Payment Period</span>
                <span className="payout-detail-value">{selectedPayout.period}</span>
              </div>

              <div className="payout-detail-row">
                <span className="payout-detail-label">Remittance Date</span>
                <span className="payout-detail-value">{selectedPayout.remittanceDate || '-'}</span>
              </div>

              <div className="payout-detail-row">
                <span className="payout-detail-label">Transaction ID</span>
                <span className="payout-detail-value">{selectedPayout.transactionId || '-'}</span>
              </div>

              <div className="payout-detail-row">
                <span className="payout-detail-label">Total Rides</span>
                <span className="payout-detail-value">{selectedPayout.totalRides}</span>
              </div>

              <div className="payout-detail-row">
                <span className="payout-detail-label">Total Earnings</span>
                <span className="payout-detail-value">{formatCurrency(selectedPayout.totalEarnings)}</span>
              </div>

              <div className="payout-detail-row">
                <span className="payout-detail-label">Deduction</span>
                <span className="payout-detail-value">-{formatCurrency(selectedPayout.deduction)}</span>
              </div>

              <div className="payout-detail-row net-payout">
                <span className="payout-detail-label">Net Payout</span>
                <span className="payout-detail-value">{formatCurrency(selectedPayout.totalPayout)}</span>
              </div>

              <div className="payout-detail-row">
                <span className="payout-detail-label">Status</span>
                <span className={`payouts-status-badge ${getStatusBadgeClass(selectedPayout.status)}`}>
                  {getStatusDisplay(selectedPayout.status)}
                </span>
              </div>

              {selectedPayout.releasedAt && (
                <div className="payout-detail-row">
                  <span className="payout-detail-label">Released At</span>
                  <span className="payout-detail-value">{selectedPayout.releasedAt}</span>
                </div>
              )}

              {/* Booking Items */}
              {selectedPayout.items && selectedPayout.items.length > 0 && (
                <div className="payout-items-section">
                  <h4 className="payout-items-title">Bookings ({selectedPayout.itemsCount})</h4>
                  <div className="payout-items-list">
                    {selectedPayout.items.map((item, index) => (
                      <div key={item.Id || index} className="payout-item-row">
                        <span className="payout-item-booking">{item.BookingNumber}</span>
                        <span className="payout-item-amount">{formatCurrency(item.Amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        show={customAlert.show}
        onClose={closeCustomAlert}
        type={customAlert.type}
        title={customAlert.title}
        message={customAlert.message}
      />
    </div>
  );
};

export default Payouts;
